import {
  CreateAgendaSlotBulkApiResponseError,
  getDayOfWeekOf,
  HttpErrors,
  PeriodOfTheDay,
  workScheduleBetweenItems,
} from '@omecare/utils';
import { format } from 'date-fns/format';
import { OpenOfficeAgendaSlotFormValues } from '@/components/forms/open-office-agenda-slot/schema';
import { useOpenOfficeAgendaSlotForm } from '@/components/forms/open-office-agenda-slot/use-open-office-agenda-slot-form';
import { pushModal } from '@/components/modals';
import useOpenOfficeAgendaSlotBulkMutation from '@/hooks/mutations/use-open-office-agenda-slot-bulk';
import { OfficeDoctorAttendingItem } from '@/hooks/queries/use-get-office';
import { SheetViewModel } from '../sheet.viewmodel';

export const selectAddOfficeAgendaSlotSheetViewModel = ({
  sheetViewModel,
  officeId,
  doctorAttendings,
  startDate,
}: {
  sheetViewModel: SheetViewModel;
  officeId: string;
  doctorAttendings: OfficeDoctorAttendingItem[];
  startDate?: Date;
}) => {
  const { openOfficeAgendaSlotForm } = useOpenOfficeAgendaSlotForm({
    doctorAttendingId:
      doctorAttendings.length > 0 ? doctorAttendings[0]!.id : undefined,
    specificDates: startDate ? [startDate] : [],
  });

  const openOfficeAgendaSlotBulkMutation =
    useOpenOfficeAgendaSlotBulkMutation();

  const submitOpenSlot = async (values: OpenOfficeAgendaSlotFormValues) => {
    // AA/SA + retrocession rate are intentionally omitted — the API resolves
    // them at read time from the assistant planning + office config, and
    // freezes them onto the slot row at booking time.

    // Transform allday selections into morning + afternoon slots
    const slots = values.specificDates.flatMap((date) => {
      const dayOfWeek = getDayOfWeekOf(date);
      const formattedDate = format(date, 'yyyy-MM-dd');
      const tzOffset = date.getTimezoneOffset();

      if (values.periodOfTheDay === 'allday') {
        // Create two slots: morning + afternoon
        const morningPeriod = workScheduleBetweenItems.find(
          (item) => item.id === 'morning',
        )!;
        const afternoonPeriod = workScheduleBetweenItems.find(
          (item) => item.id === 'afternoon',
        )!;

        return [
          {
            dayOfWeek,
            periodOfTheDay: 'morning' as PeriodOfTheDay,
            startAt: {
              date: formattedDate,
              tzOffset,
              between: morningPeriod.value!,
            },
          },
          {
            dayOfWeek,
            periodOfTheDay: 'afternoon' as PeriodOfTheDay,
            startAt: {
              date: formattedDate,
              tzOffset,
              between: afternoonPeriod.value!,
            },
          },
        ];
      } else {
        // Single slot for morning or afternoon
        const workPeriod = workScheduleBetweenItems.find(
          (workSchedule) => workSchedule.id === values.periodOfTheDay,
        )!;

        return [
          {
            dayOfWeek,
            periodOfTheDay: workPeriod.id as PeriodOfTheDay,
            startAt: {
              date: formattedDate,
              tzOffset,
              between: workPeriod.value!,
            },
          },
        ];
      }
    });

    openOfficeAgendaSlotBulkMutation.mutate(
      {
        officeId,
        doctorAttendingId: values.doctorAttendingId,
        slotType: values.slotType,
        forceWithoutAssistant: values.forceWithoutAssistant,
        slots,
      },
      {
        onSuccess(response) {
          sheetViewModel.api.close({
            success: true,
            data: response.data,
          });
        },
        onError: (error: HttpErrors) => {
          if ('data' in error) {
            sheetViewModel.api.hide();
            pushModal('CreateAgendaSlotBulkReportModal', {
              report: {
                error: error.data as CreateAgendaSlotBulkApiResponseError,
              },
              officeId,
              onClose: () => {
                sheetViewModel.api.show();
              },
            });
          }
        },
      },
    );
  };

  const submit = () => {
    openOfficeAgendaSlotForm.trigger();
    openOfficeAgendaSlotForm.handleSubmit(submitOpenSlot)();
  };

  return {
    form: openOfficeAgendaSlotForm,
    submit,
    submitting: openOfficeAgendaSlotBulkMutation.isPending,
    loading: false,
    error: null,
  };
};
